<script setup>
import { computed, onUnmounted, ref, watch } from "vue";
import { Message, MessageBox } from "element-ui";
import { useGetters, useActions } from "vuex-composition-helpers";
import DateTimeHelper from "@/common/DateTimeHelper";
import AtsLogo from "@/components/AtsLogo/AtsLogo";
import ProfilesApi from "@/common/http/ProfilesApi";
import { useRoot } from "@/composables/misc/useRoot";
import { actionTooltip, describeApplyFailure } from "./applyFailure";
import { movedFromNote } from "./movedFrom";

const root = useRoot();

const {
  currentCandidate,
} = useGetters([
  "currentCandidate",
]);

const {
  reloadProfileApplications,
} = useActions([
  "reloadProfileApplications",
]);

const applications = computed(() => {
  return currentCandidate.value.applications || [];
});

// Available stage/reject actions per atsJobId, fetched from the backend.
// Empty for a job means the current user has no allowed actions for it.
const stageActionsByJob = ref({});
const applyingJobIds = ref([]);
const appliedActionNamesByJob = ref({});

// Bumped on every fetch so a late/overlapping run (the candidate load fires several
// mergeCurrentCandidate updates) can be ignored instead of wiping fresh results.
let fetchToken = 0;

// What the offered actions actually depend on: whose applications these are, which jobs, and the state
// the server keys the actions off. Watching the applications array itself would refetch on every
// verification poll tick, since each reload replaces the array with an equal-but-new reference.
const stageActionsInputs = computed(() => [
  currentCandidate.value.profileId,
  ...applications.value.map(a => `${a.atsJobId}:${a.stageVerified}:${a.archiveReason}`),
].join("|"));

watch(stageActionsInputs, fetchStageActions, { immediate: true });

function fetchStageActions() {
  const profileId = currentCandidate.value.profileId;
  const token = ++fetchToken;

  // Clear immediately so a previous candidate's actions can't be applied to the new one
  // while this fetch is in flight (both candidates may share an atsJobId).
  stageActionsByJob.value = {};
  appliedActionNamesByJob.value = {};

  if (!profileId) {
    return;
  }

  // One batched request returns actions for all of the profile's non-archived applications,
  // keyed by atsJobId. Avoids a request per job (some candidates have dozens).
  ProfilesApi.atsReviewerStageActionsForProfile({ profileId }).then(({ data }) => {
    if (token !== fetchToken || currentCandidate.value.profileId !== profileId) {
      return;
    }

    stageActionsByJob.value = data.actionsByJob || {};
    appliedActionNamesByJob.value = {};
  }).catch(() => {});
}

// Actions per job, sorted once (advancements first, rejections last). A computed so the up-to-~20
// items don't each re-sort a fresh array on every render (the template calls actionsForJob per row
// and isFirstRejection per item).
const sortedActionsByJob = computed(() => {
  const result = {};
  for (const [atsJobId, actions] of Object.entries(stageActionsByJob.value)) {
    result[atsJobId] = [...actions].sort((a, b) => Number(a.isRejection) - Number(b.isRejection));
  }
  return result;
});

function actionsForJob(application) {
  return sortedActionsByJob.value[application.atsJobId] || [];
}

function isFirstRejection(application, action) {
  return action.isRejection && actionsForJob(application).find(a => a.isRejection) === action;
}

function selectAction(application, action) {
  if (action.isRejection) {
    MessageBox.confirm(
      `Reject this application with "${action.name}"?`,
      "Confirm rejection",
      { confirmButtonText: "Reject", cancelButtonText: "Cancel", type: "warning" }
    ).then(() => applyAction(application, action)).catch(() => {});
  } else {
    applyAction(application, action);
  }
}

async function applyAction(application, action) {
  // Send the exact application the row shows; the backend validates the ref against the profile's events and
  // the job against where the application is now before using either (so neither can be spoofed or
  // ambiguous). Capture the profile so a candidate switch mid-request can be detected.
  const profileId = currentCandidate.value.profileId;
  applyingJobIds.value = [...applyingJobIds.value, application.atsJobId];

  try {
    await ProfilesApi.applyAtsReviewerStageAction({
      profileId,
      atsJobId: application.atsJobId,
      applicationAtsRef: application.applicationAtsRef,
      actionType: action.actionType,
      actionId: action.id,
    });

    // The candidate may have changed while the request was in flight; don't paint one candidate's
    // result onto another. The push already happened server-side regardless.
    if (currentCandidate.value.profileId !== profileId) {
      return;
    }

    // Show the stage the candidate moves to (e.g. "Onsite" / "Rejected"), not the action's label
    // (e.g. "Advance to Onsite"). Falls back to the label for any action without a target stage.
    appliedActionNamesByJob.value = { ...appliedActionNamesByJob.value, [application.atsJobId]: action.targetStageName || action.name };
    stageActionsByJob.value = Object.fromEntries(
      Object.entries(stageActionsByJob.value).filter(([atsJobId]) => String(atsJobId) !== String(application.atsJobId))
    );
    Message({ type: "success", message: "Update sent to the ATS. Confirming the new stage." });

    // Refetch so the server-side pending override backs the badge (survives reload until the ATS syncs),
    // then keep watching until the backend confirms the stage against the ATS. Poll even if the refetch
    // failed — the badge is already pending from the local value, so skipping it would strand it there.
    reloadProfileApplications().finally(() => {
      // Hand the refreshed candidate to the list behind the sidebar, so its row carries the same
      // applications this tab now shows instead of the ones it was opened with.
      root?.$bus?.$emit("candidate-list-item-changed", { ...currentCandidate.value });
      // ...and reload the list itself, because the row's status column reads the indexed touchpoint rather
      // than these applications, so the emit above changes nothing the recruiter can see. The index carries
      // the pending override (not just confirmed stages), and the reindex is enqueued by the same request
      // this awaited, so by the time the debounced reload fires the new stage is usually there; when it
      // isn't, the confirmation below reloads again. Reloading here rather than only on confirmation is what
      // covers applying and then closing the sidebar — the normal way to work down a list.
      refreshCandidateList();
      startVerificationPolling();
    });
  } catch (error) {
    if (currentCandidate.value.profileId === profileId) {
      reportApplyFailure(error);
    }
  } finally {
    applyingJobIds.value = applyingJobIds.value.filter(id => id !== application.atsJobId);
  }
}

// Reload the candidate list and its pipeline counts. There is no per-row refetch — a row comes from the
// search index — so this is the whole list, which the handler debounces (3s), coalescing the reload fired
// when an action is applied with the one fired when the ATS confirms it.
function refreshCandidateList() {
  root?.$bus?.$emit("candidate-list-load-initial-candidates");
}

function reportApplyFailure(error) {
  // duration/showClose come from describeApplyFailure so the toast's timing is decided (and tested) there;
  // both are finite, so a failed push can't strand a toast under the sidebar drawer.
  const { message, duration, showClose, refetchActions, refetchApplications } = describeApplyFailure(error);

  if (refetchActions) {
    fetchStageActions();
  }
  if (refetchApplications) {
    reloadProfileApplications();
  }

  Message({ type: "error", message, duration, showClose });
}

function displayStage(application) {
  return appliedActionNamesByJob.value[application.atsJobId] || application.currentStage;
}

// True while the stage on screen is only what we asked the ATS for, not something it has confirmed.
// The backend sets stageVerified false for an application whose stage comes from an unconfirmed
// override; a locally-applied action counts as unconfirmed until the refetch says otherwise.
function isStagePending(application) {
  return Boolean(appliedActionNamesByJob.value[application.atsJobId]) || application.stageVerified === false;
}

// Who moved this candidate from here, and when. The ATS credits every move to the integration user, so
// this is the only place the acting recruiter is visible. Backed by the pending override while it lives,
// then by the durable action log once the ATS syncs and cleanup deletes that row — so the name outlives
// the sync. It still isn't full history: only the latest action shows, and only while that action is the
// one that produced the stage now displayed (otherwise the server sends no name rather than credit an
// ATS-side move to the last Teamable actor). The History tab carries the whole sequence.
function stageChangeSummary(application) {
  if (!application.stageChangedAt) {
    return null;
  }

  const who = application.stageChangedBy ? ` by ${application.stageChangedBy}` : "";
  const when = DateTimeHelper.exactDateTimeInTimezone(application.stageChangedAt);
  // displayStage, not currentStage: during the optimistic window the badge shows the action's own label
  // and currentStage is still the pre-action value, so the two would name different stages.
  const base = `Moved to ${displayStage(application)}${who} on ${when}`;

  if (isStagePending(application)) {
    return `${base} — sent to the ATS, confirming this stage`;
  }
  if (application.stageConfirmedAt) {
    return `${base} — confirmed by the ATS on ${DateTimeHelper.exactDateTimeInTimezone(application.stageConfirmedAt)}`;
  }
  return base;
}

// The tooltip has something to say for a pending stage even with no attribution (an override written by
// an older release carries no user_id), so fall back to the original pending-only wording.
function stageTooltip(application) {
  return stageChangeSummary(application) || (isStagePending(application) ? "Sent to the ATS — confirming this stage" : null);
}

// An aria-label replaces the badge's own text for a screen reader, so it has to name the stage itself.
// The summary already does ("Moved to Offer by …"); the pending-only fallback has to add it back.
function stageAriaLabel(application) {
  return stageChangeSummary(application) ||
    (isStagePending(application) ? `${displayStage(application)} — sent to the ATS, confirming this stage` : null);
}

// The backend confirms an applied action against the ATS in a background job, on a ladder: a first read
// after AtsReviewerStageActionsService::VERIFY_AFTER (1 min), then up to four more at
// AtsStageOverrideVerifyWorker::RETRY_IN (2 min) apart. Poll across that whole window so a sidebar left
// open updates itself — a shorter cap would strand the badge pending on any confirmation that lands after
// it, which is the case the ladder exists for. Past the window the override is unconfirmable and
// AtsStageOverride::UNVERIFIED_TTL drops it, so there's nothing left to wait for.
const POLL_FOR_MS = 11 * 60 * 1000;
// Most confirmations land on the first backend read, so check often at first rather than holding a 15s
// cadence — and its extra fetchStageActions request — for eleven minutes.
const POLL_FAST_EVERY_MS = 15000;
const POLL_FAST_UNTIL_MS = 90 * 1000;
const POLL_SLOW_EVERY_MS = 45000;

let pollTimer = null;
// Bumped by every stop, so a reload still in flight from a superseded cycle can tell it no longer owns
// the timer and leave the current cycle alone.
let pollGeneration = 0;
// The candidate whose cycle is running, so the watch below doesn't start a second one alongside it. Not
// pollTimer: that's null while a reload is in flight, which is exactly when applications changes.
let pollingFor = null;
// Candidates whose polling already ran out the clock. An unconfirmed override outlives the cap (it
// survives until UNVERIFIED_TTL), so without this the watch below would re-arm a fresh cycle on every
// applications change and the cap wouldn't hold. Keyed by profileId rather than reset on candidate
// switch: navigating away and back is the ordinary way to browse, and clearing it there would restart
// the whole window each time — only a fresh action deserves a fresh clock.
const pollExhausted = new Set();

function stopVerificationPolling() {
  pollGeneration += 1;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  pollingFor = null;
}

function startVerificationPolling() {
  stopVerificationPolling();

  const generation = pollGeneration;
  const startedAt = Date.now();
  const profileId = currentCandidate.value.profileId;
  pollingFor = profileId;
  // A fresh action deserves a fresh clock, even if a previous override for this candidate timed out.
  pollExhausted.delete(profileId);

  const scheduleNext = () => {
    const delay = Date.now() - startedAt < POLL_FAST_UNTIL_MS ? POLL_FAST_EVERY_MS : POLL_SLOW_EVERY_MS;
    pollTimer = setTimeout(poll, delay);
  };

  async function poll() {
    pollTimer = null;

    // Stop on candidate switch, once everything is confirmed, or after the cap — verification may
    // never succeed (the ATS can refuse the step), and this must not poll forever.
    if (generation !== pollGeneration) {
      return;
    }
    if (currentCandidate.value.profileId !== profileId) {
      stopVerificationPolling();
      return;
    }
    if (Date.now() - startedAt > POLL_FOR_MS) {
      pollExhausted.add(profileId);
      stopVerificationPolling();
      return;
    }
    if (!applications.value.some(isStagePending)) {
      stopVerificationPolling();
      return;
    }

    const refreshed = await reloadProfileApplications();

    if (generation !== pollGeneration) {
      return;
    }
    if (currentCandidate.value.profileId !== profileId) {
      stopVerificationPolling();
      return;
    }
    // Only a refresh that actually landed says anything. Treating a failed one as confirmation would read
    // stale applications as verified, clear the optimistic badge and stop — reverting the stage on screen
    // to the one the recruiter just moved the candidate off.
    if (refreshed && !applications.value.some(a => a.stageVerified === false)) {
      // A confirmed stage comes back on the application itself, so drop the local optimistic value and
      // re-fetch the actions now available from the stage the candidate actually reached.
      stopVerificationPolling();
      appliedActionNamesByJob.value = {};
      fetchStageActions();
      // Reload again now that the stage is the ATS's own rather than the override's claim: the verify worker
      // reindexes as it confirms, and what it confirmed can differ from what was applied (a step the
      // spreadsheet called an advancement can land on a disposition).
      refreshCandidateList();
      return;
    }

    scheduleNext();
  }

  scheduleNext();
}

onUnmounted(stopVerificationPolling);
// A candidate switch invalidates any in-flight polling; the new candidate starts its own if needed. The
// exhaustion set deliberately survives — see pollExhausted.
watch(() => currentCandidate.value.profileId, stopVerificationPolling);

// An override can already be unconfirmed when the applications load — a reopened sidebar, a page
// refresh, or an action applied from another tab. Without this, only a fresh local action starts the
// timer, so the badge would sit pending until the user acted again. Skipped once this candidate's
// polling has run out the clock, so the cap isn't restarted by every subsequent reload.
watch(applications, apps => {
  if (pollingFor !== null || pollExhausted.has(currentCandidate.value.profileId)) {
    return;
  }
  if (apps.some(isStagePending)) {
    startVerificationPolling();
  }
}, { immediate: true });

function getStageType(archiveReason) {
  if (archiveReason === "HIRED") {
    return "positive";
  }

  const negativeReasons = ["WITHDREW", "REJECTED", "NOT_HIRED"];
  if (negativeReasons.includes(archiveReason)) {
    return "negative";
  }

  return "neutral";
}
</script>

<template>
  <div id="tab-applications">
    <div v-if="applications.length > 0" class="applications-container">
      <ul>
        <li v-for="application in applications" :key="application.atsJobId" class="application-item">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="project-icon-wrapper mr-8">
                <AtsLogo
                  v-if="application.atsJobId"
                  :ats-name="application.atsJobAtsName"
                  :problem="application.atsJobProblem"
                />
                <img v-else class="project-icon" :class="{ 'is-link': application.projectId }" :src="require('@/assets/images/icons/briefcase.svg')" />
              </div>
              <div>
                <div class="d-flex align-items-center">
                  <router-link
                    v-if="application.projectId"
                    :to="{ name: 'job-root', params: { projectId: application.projectId } }"
                    class="application-link"
                  >
                    {{ application.jobName || application.projectName }} <span v-if="application.reqId" class="req-id">({{ application.reqId }})</span>
                  </router-link>
                  <span v-else class="application-job-name">
                    {{ application.jobName || application.projectName }} <span v-if="application.reqId" class="req-id">({{ application.reqId }})</span>
                  </span>
                </div>
                <div v-if="movedFromNote(application)" class="application-moved-from">
                  {{ movedFromNote(application) }}
                </div>
                <div v-if="application.appliedDate || application.archiveDescription" class="application-meta">
                  <el-tooltip v-if="application.appliedDate" :content="DateTimeHelper.exactDateTimeInTimezone(application.appliedDate)">
                    <span class="applied-date cursor-help">
                      Applied {{ DateTimeHelper.relativeTimeFromNow(application.appliedDate) }}
                    </span>
                  </el-tooltip>

                  <template v-if="application.archiveDescription">
                    <el-divider v-if="application.appliedDate" direction="vertical" class="meta-divider" />
                    <span class="archive-description">
                      {{ application.archiveDescription }}

                      <el-tooltip
                        v-if="application.lastActivityAt"
                        :content="DateTimeHelper.exactDateTimeInTimezone(application.lastActivityAt)"
                      >
                        <span class="cursor-help">
                          ({{ DateTimeHelper.relativeTimeFromNow(application.lastActivityAt) }})
                        </span>
                      </el-tooltip>
                    </span>
                  </template>

                  <template v-if="application.archiveNote">
                    <el-divider direction="vertical" class="meta-divider" />
                    <span class="archive-description">{{ application.archiveNote }}</span>
                  </template>
                </div>
              </div>
            </div>
            <div v-if="displayStage(application) || application.projectStatus || actionsForJob(application).length" class="application-status-badges">
              <el-dropdown
                v-if="actionsForJob(application).length"
                trigger="click"
                class="stage-actions-dropdown"
                @command="action => selectAction(application, action)"
              >
                <el-button size="mini" :loading="applyingJobIds.includes(application.atsJobId)">
                  Update stage<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-for="action in actionsForJob(application)"
                    :key="`${action.actionType}-${action.id}`"
                    :command="action"
                    :divided="isFirstRejection(application, action)"
                    :class="{ 'rejection-action': action.isRejection }"
                  >
                    <el-tooltip
                      :disabled="!actionTooltip(action)"
                      :content="actionTooltip(action)"
                      placement="left"
                      :open-delay="250"
                    >
                      <span>{{ action.name }}</span>
                    </el-tooltip>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>

              <el-tooltip
                v-if="displayStage(application)"
                :disabled="!stageTooltip(application)"
                :content="stageTooltip(application)"
                placement="top"
                :open-delay="250"
              >
                <span
                  class="badge badge-stage"
                  :class="[`badge-stage-${getStageType(application.archiveReason)}`, { 'badge-stage-pending': isStagePending(application) }]"
                  :aria-label="stageAriaLabel(application)"
                >
                  {{ displayStage(application) }}
                </span>
              </el-tooltip>
              <span v-else-if="application.projectStatus" class="badge badge-status">
                {{ application.projectStatus }}
              </span>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div v-else class="no-applications">
      This candidate has not applied to any jobs
    </div>
  </div>
</template>

<style scoped lang="scss">
#tab-applications {
  width: 100%;
  font-size: 14px;
  color: $tf-font-default;
  cursor: default;

  h4 {
    font-size: 20px;
    margin-bottom: 16px;
  }

  .applications-container {
    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;

      .application-item {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid $black-10;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .project-icon-wrapper {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          align-self: flex-start;
          margin-top: 2px;

          .project-icon {
            width: 100%;
            height: 100%;
            object-fit: contain;

            &.is-link {
              filter: $filter-blue-100;
            }
          }
        }

        .application-link {
          color: $blue-100;
          font-weight: 500;
          font-size: 14px;

          &:hover {
            text-decoration: none;
          }
        }

        .application-job-name {
          color: $tf-font-default;
          font-weight: 500;
          font-size: 14px;
        }

        .application-moved-from {
          margin-top: 4px;
          font-size: 12px;
          color: $tf-font-ghost;
        }

        .application-meta {
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: $tf-font-ghost;

          .meta-divider {
            margin: 0;
            height: 12px;
            background-color: $black-50;
          }

          .archive-description {
            font-weight: 500;
            color: $tf-font-default;
          }

          .applied-date {
            color: $tf-font-ghost;
          }
        }

        .req-id {
          color: $tf-font-ghost;
          font-weight: 400;
          font-size: 12px;
        }

        .application-status-badges {
          display: flex;
          gap: 8px;
          align-items: center;

          .badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            white-space: nowrap;
          }

          .badge-stage {
            background-color: $black-10;
            color: $tf-font-default;

            &.badge-stage-positive {
              background-color: $green-10;
              color: $green-100;
            }

            &.badge-stage-negative {
              background-color: $magenta-10;
              color: $magenta-100;
            }

            &.badge-stage-neutral {
              background-color: $black-10;
              color: $tf-font-default;
            }

            // The stage was sent to the ATS but not yet confirmed by it. Dashed outline rather than a
            // different colour, so the stage's own positive/negative meaning still reads.
            &.badge-stage-pending {
              border: 1px dashed $black-30;
              opacity: 0.75;
            }
          }

          .badge-status {
            background-color: $green-10;
            color: $green-100;
          }
        }
      }
    }
  }

  .no-applications {
    color: $tf-font-ghost;
    font-size: 14px;
    margin-top: 8px;
  }
}
</style>

<style lang="scss">
// Not scoped: el-dropdown-menu is appended to body
.el-dropdown-menu__item.rejection-action {
  color: $magenta-100;
}
</style>
